"use client";

import PageLayout from "@/components/layout/PageLayout";
import { cn } from "@nitro-ape/shared/lib/utils";
import { Chain } from "@nitro-ape/shared/models/enums/chain";
import { translateBrand } from "@/lib/i18n";
import { BrandMessageIds } from "@nitro-ape/shared/lib/brand-config";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { HYPERLIQUID_ENABLED } from "@/lib/feature-flags";
import { DEFAULT_MONACO_PERP_SYMBOL } from "@/lib/trade-url";
import HyperliquidPerpsPairsTable from "./-components/HyperliquidPerpsPairsTable";
import MonacoPerpsPairsTable from "./-components/MonacoPerpsPairsTable";

type PerpsChain = Chain.hyperliquid | Chain.monaco_perps;

const PERPS_CHAINS: {
  id: PerpsChain;
  label: string;
  icon: string;
  disabled: boolean;
}[] = [
  {
    id: Chain.monaco_perps,
    label: "Monaco",
    icon: "/images/chains/monaco.webp",
    disabled: false,
  },
  {
    id: Chain.hyperliquid,
    label: "Hyperliquid",
    icon: "/images/chains/hl.webp",
    disabled: false,
  },
];

export const Route = createFileRoute("/_main/perpetuals/")({
  // Hyperliquid is hidden: redirect the perps markets index to the Monaco
  // perps default while the flag is off.
  beforeLoad: () => {
    if (!HYPERLIQUID_ENABLED) {
      throw redirect({
        to: "/monaco-perpetuals/$coin",
        params: { coin: DEFAULT_MONACO_PERP_SYMBOL },
      });
    }
  },
  head: () => ({
    meta: [
      {
        title: translateBrand(BrandMessageIds.metaTitlePerpetuals),
      },
    ],
  }),
  component: PerpsMarketsPage,
});

function PerpsMarketsPage() {
  const [selected, setSelected] = useState<PerpsChain>(Chain.monaco_perps);

  return (
    <PageLayout>
      <div className="flex h-full max-h-full w-full flex-col gap-1.5">
        <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
          <div className="bg-card w-max rounded-2xl p-1">
            <div className="flex flex-row gap-2">
              {PERPS_CHAINS.map((chain) => {
                const isSelected = selected === chain.id;
                return (
                  <button
                    type="button"
                    key={chain.id}
                    onClick={() => !chain.disabled && setSelected(chain.id)}
                    disabled={chain.disabled}
                    className={cn(
                      "flex items-center gap-2 rounded-xl py-2 pl-3 pr-4 text-sm transition-all",
                      chain.disabled
                        ? "cursor-not-allowed opacity-35"
                        : isSelected
                          ? "bg-card-accent text-white"
                          : "text-gray-p hover:bg-card-accent hover:text-white",
                    )}
                  >
                    <img
                      src={chain.icon}
                      alt={chain.label}
                      className={cn(
                        "h-3.5 w-3.5 rounded-sm",
                        chain.disabled && "grayscale",
                      )}
                    />
                    <span>{chain.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {selected === Chain.hyperliquid ? (
          <HyperliquidPerpsPairsTable />
        ) : (
          <MonacoPerpsPairsTable />
        )}
      </div>
    </PageLayout>
  );
}
